import React, { useEffect, useRef } from "react";
import { skills } from "../../data/skills";
import { Mask } from "./Chrome";
import { clamp, createPit } from "./ballPhysics";

const SUBSTEPS = 2;

/* Skills as a pit of balls. They drop in when the section scrolls into view,
   lift and slosh with scroll speed, and scatter from the cursor. With reduced
   motion the CSS lays the same list out as a static wrapped grid instead. */
function Stack() {
  const pitRef = useRef(null);

  useEffect(() => {
    const pit = pitRef.current;
    if (!pit || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const els = [...pit.querySelectorAll(".ball")];
    const sim = createPit(skills.map((skill) => skill.size));
    const tilts = els.map(() => 0);
    const pointer = { x: 0, y: 0, vx: 0, vy: 0, time: 0, active: false };
    let raf = 0;
    let dropped = false;
    let last = 0;
    let lastScroll = window.scrollY;
    let scrollSpeed = 0;

    const layout = () => {
      sim.resize(pit.clientWidth, pit.clientHeight);
      sim.balls.forEach((b, i) => els[i].style.setProperty("--d", `${b.r * 2}px`));
    };

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min((now - last) / 1000, 1 / 30);
      last = now;
      if (dt <= 0) return;

      const rawSpeed = (window.scrollY - lastScroll) / dt;
      lastScroll = window.scrollY;
      scrollSpeed += (rawSpeed - scrollSpeed) * 0.3;

      for (let i = 0; i < SUBSTEPS; i++) sim.step(dt / SUBSTEPS, scrollSpeed, pointer);
      pointer.vx *= 0.85;
      pointer.vy *= 0.85;

      sim.balls.forEach((b, i) => {
        // Lean into the direction of travel, settle upright so labels stay readable
        tilts[i] += (clamp(b.vx * 0.05, -18, 18) - tilts[i]) * 0.2;
        els[i].style.transform = `translate3d(${b.x - b.r}px, ${b.y - b.r}px, 0) rotate(${tilts[i]}deg)`;
      });
    };

    const start = () => {
      if (raf) return;
      if (!dropped) {
        pit.classList.add("ballpit--live");
        layout();
        sim.drop();
        dropped = true;
      }
      last = performance.now();
      lastScroll = window.scrollY;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    // Simulate only while the pit is on screen
    const observer = new IntersectionObserver(([entry]) => (entry.isIntersecting ? start() : stop()), {
      threshold: 0.15,
    });
    observer.observe(pit);

    const onPointer = (e) => {
      const rect = pit.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const elapsed = (e.timeStamp - pointer.time) / 1000;
      if (pointer.active && elapsed > 0 && elapsed < 0.1) {
        pointer.vx = clamp((x - pointer.x) / elapsed, -3000, 3000);
        pointer.vy = clamp((y - pointer.y) / elapsed, -3000, 3000);
      }
      pointer.x = x;
      pointer.y = y;
      pointer.time = e.timeStamp;
      pointer.active = true;
    };
    const onLeave = () => {
      pointer.active = false;
    };
    const onResize = () => {
      if (dropped) layout();
    };

    pit.addEventListener("pointermove", onPointer);
    pit.addEventListener("pointerdown", onPointer);
    pit.addEventListener("pointerleave", onLeave);
    pit.addEventListener("pointerup", onLeave);
    window.addEventListener("resize", onResize);

    return () => {
      stop();
      observer.disconnect();
      pit.removeEventListener("pointermove", onPointer);
      pit.removeEventListener("pointerdown", onPointer);
      pit.removeEventListener("pointerleave", onLeave);
      pit.removeEventListener("pointerup", onLeave);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <section className="section stack" aria-labelledby="stack-title">
      <div className="wrap">
        <h2 id="stack-title" className="section-title">
          <Mask>
            My <em>tech stack</em>
          </Mask>
        </h2>
        <p className="stack-hint" data-reveal>
          Scroll or sweep your cursor through them.
        </p>

        <ul className="ballpit" ref={pitRef} aria-label="Skills and tools">
          {skills.map((skill) => (
            <li
              key={skill.label}
              className={`ball ${skill.tone ? `ball--${skill.tone}` : ""}`}
              style={{ "--d": `${skill.size}px`, "--logo": skill.color }}
            >
              {skill.path && (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={skill.path} />
                </svg>
              )}
              <span>{skill.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default Stack;
